import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import localizedFormat from "dayjs/plugin/localizedFormat";
import advancedFormat from "dayjs/plugin/advancedFormat";
import customParseFormat from "dayjs/plugin/customParseFormat";
import isBetween from "dayjs/plugin/isBetween";
import isYesterday from "dayjs/plugin/isYesterday";
import isToday from "dayjs/plugin/isToday";
import isTomorrow from "dayjs/plugin/isTomorrow";
import relativeTime from "dayjs/plugin/relativeTime";
import dayjsDuration from "dayjs/plugin/duration";
import "dayjs/locale/en";
import {
  BEGINNING_OF_TIME,
  DATE_FORMAT_ONLY_TIME,
  DATE_FORMAT_ONLY_TIME_WITH_SECONDS,
  DATE_FORMAT_WITHOUT_TIME,
} from "shared/lib/constants/time";

import { DATE_FORMAT } from "shared/lib/constants/time";
import {
  US_ZONE_NAME_TO_TZ_CODE,
  UsZoneName,
} from "shared/lib/constants/timezones";
import { checkIfClient } from "./utils";

dayjs.extend(localizedFormat);
dayjs.extend(customParseFormat);
dayjs.extend(isBetween);
dayjs.extend(isYesterday);
dayjs.extend(isToday);
dayjs.extend(isTomorrow);
dayjs.extend(relativeTime);
dayjs.extend(dayjsDuration);

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);

export const DayJs = dayjs;

export const safeGetDate = (
  date: string | Date | Dayjs | undefined,
  format: string = DATE_FORMAT,
  timezone?: string
) => {
  if (checkIfClient()) {
    let dayObject = date ? dayjs(date) : dayjs();

    if (timezone) {
      dayObject = dayObject.tz(timezone);
    }

    return dayObject.format(format);
  }

  return "";
};

export const isDifferentDay = (date1: string, date2: string) => {
  const formattedDate1 = dayjs(date1).format("YYYY-MM-DD");
  const formattedDate2 = dayjs(date2).format("YYYY-MM-DD");

  return formattedDate1 !== formattedDate2;
};

export interface TimeDurationsI {
  hours?: number;
  minutes?: number;
  days?: number;
}

export const convertTimeToNormalizedDuration = ({
  hours: _hours = 0,
  minutes: _minutes = 0,
  days: _days = 0,
}: TimeDurationsI) => {
  const totalHours = _hours + _days * 24 + _minutes / 60;

  const days = Math.floor(totalHours / 24);
  const remainingHours = totalHours % 24;
  const remainingMinutes = Math.round(
    (remainingHours - Math.floor(remainingHours)) * 60
  );

  return {
    days: days,
    hours: Math.floor(remainingHours),
    minutes: remainingMinutes,
  };
};

export const convertSecondsToHoursMinutesSeconds = (totalSeconds: number) => {
  const hours = Math.floor(totalSeconds / 3600);
  const remainingSecondsAfterHours = totalSeconds % 3600;
  const minutes = Math.floor(remainingSecondsAfterHours / 60);
  const seconds = Math.round(remainingSecondsAfterHours % 60);

  return {
    hours,
    minutes,
    seconds,
  };
};

// https://stackoverflow.com/questions/15083548/convert-12-hour-hhmm-am-pm-to-24-hour-hhmm
export const convertTime12to24 = (time12h: string) => {
  const [time, modifier] = time12h.split(" ");

  const timeDecomposed = time.split(":");

  let hours = timeDecomposed[0];
  const minutes = timeDecomposed[1];

  if (hours === "12") {
    hours = "00";
  }

  if (modifier.toLocaleLowerCase() === "pm") {
    hours = (parseInt(hours, 10) + 12).toString();
  }

  return {
    hours: parseInt(hours),
    minutes: parseInt(minutes),
    formatted: `${hours}:${minutes}`,
  };
};

/**
 * Combines a calendar date, a 12-hour time option and a US timezone label
 * (booking dropdown value) into the exact instant the user meant.
 *
 * The zone is resolved through US_ZONE_NAME_TO_TZ_CODE - never by lookup over
 * the TIMEZONES array, whose fullZoneName values are ambiguous and resolve to
 * non-DST regions (GLE-8091). The wall time is interpreted with dayjs.tz()
 * rather than .tz(code, true): keepLocalTime applies the offset of the source
 * instant, which is wrong on DST transition days.
 *
 * @param date calendar date as Date.toString() output (react-calendar) or
 *   "YYYY-MM-DD". Must NOT be an offset-bearing ISO datetime - callers that
 *   already hold a combined ISO should use DayJs(iso) directly.
 * @param time12 "h:mm am|pm" time option.
 * @param zoneName a US_ZONE_NAME_TO_TZ_CODE key.
 * @throws Error on unknown zoneName or unparseable date: both can only be
 *   programming errors (the values come from our own dropdowns).
 */
export const combineDateTimeWithZone = (
  date: string,
  time12: string,
  zoneName: string
): Dayjs => {
  const tzCode = US_ZONE_NAME_TO_TZ_CODE[zoneName as UsZoneName];

  if (!tzCode) {
    throw new Error(`combineDateTimeWithZone: unknown zone name "${zoneName}"`);
  }

  const day = dayjs(date);

  if (!day.isValid()) {
    throw new Error(`combineDateTimeWithZone: invalid date "${date}"`);
  }

  const { hours, minutes } = convertTime12to24(time12);

  // Not dayjs.tz(): it seeds its offset guess with the zone's offset NOW, so
  // an ambiguous fall-back wall time (1:30 am twice) resolved to the first
  // occurrence while the zone was on daylight time and to the second once it
  // was on standard time. Seed with the offset a day earlier instead.
  const wallAsUtc = Date.UTC(
    day.year(),
    day.month(),
    day.date(),
    hours,
    minutes
  );
  const dayBeforeOffset = zoneOffsetMinutes(wallAsUtc - DAY_MS, tzCode);
  const guess = wallAsUtc - dayBeforeOffset * MINUTE_MS;
  const guessOffset = zoneOffsetMinutes(guess, tzCode);

  if (guessOffset === dayBeforeOffset) {
    // Also the first occurrence of an ambiguous fall-back time
    return dayjs(guess).utcOffset(guessOffset);
  }

  const retry = wallAsUtc - guessOffset * MINUTE_MS;

  if (zoneOffsetMinutes(retry, tzCode) === guessOffset) {
    return dayjs(retry).utcOffset(guessOffset);
  }

  // A spring-forward gap (2:30 am does not exist): keep the pre-gap offset,
  // which lands an hour later on the clock (3:30 am daylight time)
  return dayjs(guess).utcOffset(guessOffset);
};

const MINUTE_MS = 60 * 1000;
const DAY_MS = 24 * 60 * MINUTE_MS;

// The zone's UTC offset in minutes at an instant (Chicago in summer: -300).
// Intl rather than dayjs .tz(), which misreads the repeated hour when the
// host runs in the same zone.
const zoneOffsetMinutes = (instantMs: number, tzCode: string): number => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tzCode,
    hourCycle: "h23",
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
  }).formatToParts(new Date(instantMs));
  const part = (type: Intl.DateTimeFormatPartTypes): number =>
    Number(parts.find((p) => p.type === type)?.value);
  const wallAsUtc = Date.UTC(
    part("year"),
    part("month") - 1,
    part("day"),
    part("hour"),
    part("minute")
  );

  return Math.round(
    (wallAsUtc - Math.floor(instantMs / MINUTE_MS) * MINUTE_MS) / MINUTE_MS
  );
};

// An ISO 8601 date-time that pins an instant: a trailing "Z" or a numeric
// offset (+HH:MM or +HHMM). Zone-less ISO strings, "YYYY-MM-DD" and
// Date.toString() output do not match: they are wall-clock values.
const ISO_INSTANT_WITH_OFFSET =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})$/i;

/**
 * The calendar day of an instant in a US zone (a US_ZONE_NAME_TO_TZ_CODE
 * key), as a zone-less local midnight "YYYY-MM-DDT00:00:00".
 *
 * CalendarForm keeps its picked day as a string read with dayjs(date), that
 * is in the BROWSER's zone, and combines it with the time option in the
 * SELECTED zone. Seeded with an instant such as "2026-10-15T21:00:00Z" (5 pm
 * Eastern on the 15th) it picked the browser's day instead: the 16th in
 * Manila or Kyiv, so an unchanged submit landed 24 hours late. A zone-less
 * local midnight is read as the same day by dayjs() and new Date() in every
 * browser zone, which is what the form's existing date handling expects.
 *
 * Any value that is not an offset-bearing instant is returned unchanged:
 * there is no zone to convert it from. An unknown zone or an unparseable
 * instant is returned unchanged too, so the caller keeps its current
 * behavior (combineDateTimeWithZone still rejects both on submit).
 */
export const toCalendarDayInZone = (
  value: string,
  zoneName: string
): string => {
  if (!ISO_INSTANT_WITH_OFFSET.test(value)) {
    return value;
  }

  const tzCode = US_ZONE_NAME_TO_TZ_CODE[zoneName as UsZoneName];

  if (!tzCode) {
    return value;
  }

  const instant = dayjs(value);

  if (!instant.isValid()) {
    return value;
  }

  return `${instant.tz(tzCode).format("YYYY-MM-DD")}T00:00:00`;
};

export const getCurrentYear = () => dayjs().year();

export const duration = dayjs.duration;

/**
 * @param pastDate the date in the past to calculate how much time has passed since.
 * @param fallback the value to return if the pastDate is invalid.
 * @returns a human readable string describing how long ago the pastDate was. Example: 5 days ago. Returns "N/A" if the pastDate is the beginning of time.
 */
export const getTimeAgo = (pastDate: string, fallback = "N/A") => {
  if (pastDate === BEGINNING_OF_TIME) {
    return fallback;
  }

  return dayjs(pastDate).fromNow();
};

export const getNumberOfDaysBetweenDates = (date1: string, date2: string) => {
  if (!date1 || !date2) {
    return 0;
  }

  return dayjs(date1).diff(dayjs(date2), "days");
};

/**
 * Check if the current date is in daylight saving time
 *
 * getTimezoneOffset() returns the timezone offset in minutes from UTC.
 * In DST, the offset is typically smaller (e.g., -240 instead of -300 for EDT vs. EST).
 * We compare the current offset with the larger (standard) offset from January and July to determine if DST is active.
 * This works for most regions but may not cover rare edge cases (e.g., locales with irregular DST rules).
 *
 * @returns true if the current date is in daylight saving time, false otherwise
 */
export const getIsDST = () => {
  const today = new Date();
  const jan = new Date(today.getFullYear(), 0, 1); // January 1
  const jul = new Date(today.getFullYear(), 6, 1); // July 1
  const stdOffset = Math.max(jan.getTimezoneOffset(), jul.getTimezoneOffset());
  return today.getTimezoneOffset() < stdOffset;
};

// if < 1 hour, format to mm min ss sec
// if > 1 hour, format to hh hr mm min
export const formatSecondsToHoursMinutesSecondsString = (
  _seconds: number | undefined
) => {
  if (!_seconds) {
    return "0s";
  }

  const { hours, minutes, seconds } =
    convertSecondsToHoursMinutesSeconds(_seconds);

  let resultingString = "";

  if (hours > 0) {
    resultingString += `${hours}h `;
  }

  if (minutes > 0) {
    resultingString += `${minutes}m `;
  }

  if (hours < 1) {
    resultingString += `${seconds}s`;
  }

  return resultingString ? resultingString.trim() : "0s";
};

export const getFormattedLastUpdatedAt = (updatedAt: string) => {
  const isAfter1Minute = DayJs().isAfter(updatedAt, "minute");
  const isAfter1Hour = DayJs().isAfter(updatedAt, "hour");
  const isAfter1Day = DayJs().isAfter(updatedAt, "day");

  const FORMAT = isAfter1Day
    ? DATE_FORMAT_WITHOUT_TIME
    : isAfter1Hour
      ? DATE_FORMAT_ONLY_TIME
      : isAfter1Minute
        ? DATE_FORMAT_ONLY_TIME
        : DATE_FORMAT_ONLY_TIME_WITH_SECONDS;

  return DayJs(updatedAt).format(FORMAT);
};
